import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { safeApiErrorMessage } from '../utils/safeApiErrorMessage';
import {
  Alert,
  Box,
  Checkbox,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import {
  changePassword,
  confirmTwoFactor,
  disableTwoFactor,
  fetchSecurityEvents,
  fetchSessions,
  logoutOtherSessions,
  regenerateRecoveryCodes,
  revokeSession,
  setupTwoFactor,
} from '../api/authApi';
import { colorTokens, typographyTokens } from '../app/designTokens';
import { useAuth } from '../auth/AuthContext';
import { DataTable } from '../components/DataTable';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import { PageHeader } from '../components/PageHeader';
import { PasswordPolicyHints } from '../components/PasswordPolicyHints';
import { SectionCard } from '../components/SectionCard';
import { StatusBadge } from '../components/StatusBadge';
import { TableToolbar } from '../components/TableToolbar';
import { Button } from '../components/ui/Button';
import { TextInput } from '../components/ui/TextInput';
import { formatDateTime } from '../utils/formatters';
import {
  canonicalPasswordFailureI18nKeys,
  canonicalPasswordFailures,
} from '../utils/canonicalPassword';

const { semantic } = colorTokens;

const formStackSx = {
  display: 'grid',
  gap: 2,
  maxWidth: 480,
};

const noticeSx = {
  fontFamily: typographyTokens.fontFamily,
  fontSize: 13,
  lineHeight: '18px',
  color: semantic.text.secondary,
};

const checkboxSx = {
  color: semantic.border.default,
  p: 0.75,
  '&.Mui-checked': {
    color: colorTokens.brand.primary,
  },
  '&.Mui-disabled': {
    color: semantic.text.disabled,
  },
};

const rowActionSx = {
  minHeight: 32,
  px: 1,
};

export function AccountSecurityPage({ variant = 'admin' }) {
  const { t, i18n } = useTranslation(['settings', 'navigation', 'common', 'validation']);
  const { session, refreshSession } = useAuth();
  const isPortal = variant === 'portal';
  const twoFactorEnabled = Boolean(session?.user?.two_factor_enabled || session?.two_factor_enabled);
  const emptyValue = t('common:empty_value');

  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    password: '',
    password_confirmation: '',
    revoke_other_sessions: false,
  });
  const [twoFactorSetup, setTwoFactorSetup] = useState(null);
  const [confirmCode, setConfirmCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [disablePassword, setDisablePassword] = useState('');
  const [regeneratePassword, setRegeneratePassword] = useState('');
  const [sessions, setSessions] = useState([]);
  const [events, setEvents] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loggingOutOthers, setLoggingOutOthers] = useState(false);

  const passwordInvalid = canonicalPasswordFailures(
    passwordForm.password,
    passwordForm.password_confirmation,
  ).length > 0;

  const loadSessions = useCallback(() => {
    setLoadingSessions(true);
    fetchSessions()
      .then(setSessions)
      .catch(() => setError(t('settings:failed_load_sessions')))
      .finally(() => setLoadingSessions(false));
  }, [t]);

  const loadEvents = useCallback(() => {
    setLoadingEvents(true);
    fetchSecurityEvents({ per_page: 20 })
      .then((response) => setEvents(response.data || []))
      .catch(() => setError(t('settings:failed_load_security_events')))
      .finally(() => setLoadingEvents(false));
  }, [t]);

  useEffect(() => {
    loadSessions();
    loadEvents();
  }, [loadSessions, loadEvents]);

  const handlePasswordChange = async () => {
    setError('');
    setMessage('');

    const passwordFailures = canonicalPasswordFailures(
      passwordForm.password,
      passwordForm.password_confirmation,
    );
    if (passwordFailures.length > 0) {
      setError(canonicalPasswordFailureI18nKeys(passwordFailures).map((key) => t(key)).join(', '));
      return;
    }

    try {
      await changePassword(passwordForm);
      setMessage(t('settings:password_changed_success'));
      setPasswordForm({
        current_password: '',
        password: '',
        password_confirmation: '',
        revoke_other_sessions: false,
      });
      loadSessions();
      loadEvents();
    } catch (err) {
      setError(safeApiErrorMessage(err, t('settings:failed_change_password')));
    }
  };

  const handleSetupTwoFactor = async () => {
    setError('');
    setMessage('');

    try {
      const setup = await setupTwoFactor();
      setTwoFactorSetup(setup);
      setRecoveryCodes([]);
      setMessage(t('settings:two_factor_scan_hint'));
    } catch {
      setError(t('settings:failed_setup_two_factor'));
    }
  };

  const handleConfirmTwoFactor = async () => {
    setError('');
    setMessage('');

    try {
      const result = await confirmTwoFactor(confirmCode);
      setRecoveryCodes(result.recovery_codes || []);
      setTwoFactorSetup(null);
      setConfirmCode('');
      setMessage(t('settings:two_factor_enabled_success'));
      await refreshSession();
      loadEvents();
    } catch (err) {
      setError(safeApiErrorMessage(err, t('settings:failed_confirm_two_factor')));
    }
  };

  const handleDisableTwoFactor = async () => {
    setError('');
    setMessage('');

    try {
      await disableTwoFactor(disablePassword);
      setDisablePassword('');
      setTwoFactorSetup(null);
      setRecoveryCodes([]);
      setMessage(t('settings:two_factor_disabled_success'));
      await refreshSession();
      loadEvents();
    } catch (err) {
      setError(safeApiErrorMessage(err, t('settings:failed_disable_two_factor')));
    }
  };

  const handleRegenerateRecoveryCodes = async () => {
    setError('');
    setMessage('');

    try {
      const result = await regenerateRecoveryCodes(regeneratePassword);
      setRecoveryCodes(result.recovery_codes || []);
      setRegeneratePassword('');
      setMessage(t('settings:recovery_codes_regenerated'));
      loadEvents();
    } catch (err) {
      setError(safeApiErrorMessage(err, t('settings:failed_regenerate_recovery_codes')));
    }
  };

  const handleLogoutOtherSessions = async () => {
    setError('');
    setLoggingOutOthers(true);
    try {
      await logoutOtherSessions();
      loadSessions();
    } catch (err) {
      setError(safeApiErrorMessage(err, t('settings:failed_load_sessions')));
    } finally {
      setLoggingOutOthers(false);
    }
  };

  const sessionColumns = useMemo(() => [
    {
      id: 'device',
      label: t('settings:device'),
      render: (row) => (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography
            sx={{
              fontFamily: typographyTokens.fontFamily,
              fontSize: 13,
              color: semantic.text.primary,
            }}
          >
            {row.name || emptyValue}
          </Typography>
          {row.is_current ? (
            <StatusBadge
              status="current"
              category="generic"
              apiLabel={t('common:current')}
              size="Sm"
              tone="Info"
            />
          ) : null}
        </Stack>
      ),
    },
    {
      id: 'last_used',
      label: t('settings:last_used'),
      render: (row) => formatDateTime(row.last_used_at, i18n.language) || emptyValue,
    },
    {
      id: 'created',
      label: t('settings:created'),
      render: (row) => formatDateTime(row.created_at, i18n.language) || emptyValue,
    },
    {
      id: 'actions',
      label: t('common:actions'),
      align: 'right',
      render: (row) => (
        !row.is_current
          ? (
            <Button
              variant="Ghost"
              onClick={() => revokeSession(row.id).then(loadSessions)}
              sx={rowActionSx}
            >
              {t('settings:revoke')}
            </Button>
          )
          : emptyValue
      ),
    },
  ], [emptyValue, i18n.language, loadSessions, t]);

  const eventColumns = useMemo(() => [
    {
      id: 'event',
      label: t('settings:event'),
      render: (row) => (
        <Typography
          sx={{
            fontFamily: typographyTokens.fontFamilyMono,
            fontSize: 12,
            color: semantic.text.primary,
            overflowWrap: 'anywhere',
          }}
        >
          {row.event_type || emptyValue}
        </Typography>
      ),
    },
    {
      id: 'date',
      label: t('settings:date'),
      render: (row) => formatDateTime(row.created_at, i18n.language) || emptyValue,
    },
    {
      id: 'ip',
      label: t('settings:ip'),
      render: (row) => row.ip_address || t('common:not_available'),
    },
    {
      id: 'user_agent',
      label: t('settings:user_agent'),
      render: (row) => (
        <Typography
          sx={{
            fontFamily: typographyTokens.fontFamily,
            fontSize: 12,
            color: semantic.text.secondary,
            maxWidth: 320,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={row.user_agent || undefined}
        >
          {row.user_agent || t('common:not_available')}
        </Typography>
      ),
    },
  ], [emptyValue, i18n.language, t]);

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <PageHeader
        type={isPortal ? 'List' : 'Detail'}
        title={t('navigation:account_security')}
        subtitle={isPortal ? t('settings:security_page_subtitle') : t('settings:language_hint')}
        breadcrumb={isPortal
          ? `${t('navigation:portal_dashboard')} / ${t('navigation:account_security')}`
          : undefined}
      />

      {message && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>
          {message}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Stack spacing={2.5}>
        <SectionCard variant="Default" title={t('settings:language_section')} sx={{ mb: 0 }}>
          {isPortal ? (
            <LanguageSwitcher
              variant="select"
              persistToApi
              label={t('settings:language_section')}
              helperText={t('settings:language_hint')}
            />
          ) : (
            <>
              <Typography sx={{ ...noticeSx, mb: 2 }}>
                {t('settings:language_hint')}
              </Typography>
              <LanguageSwitcher
                variant="select"
                persistToApi
                label={t('settings:language_section')}
              />
            </>
          )}
        </SectionCard>

        <SectionCard variant="Default" title={t('settings:password_section')} sx={{ mb: 0 }}>
          <Box sx={formStackSx}>
            <TextInput
              density="Compact"
              type="password"
              label={t('settings:current_password')}
              value={passwordForm.current_password}
              onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
              autoComplete="current-password"
            />
            <TextInput
              density="Compact"
              type="password"
              label={t('settings:new_password')}
              value={passwordForm.password}
              onChange={(e) => setPasswordForm({ ...passwordForm, password: e.target.value })}
              autoComplete="new-password"
            />
            <TextInput
              density="Compact"
              type="password"
              label={t('settings:confirm_new_password')}
              value={passwordForm.password_confirmation}
              onChange={(e) => setPasswordForm({ ...passwordForm, password_confirmation: e.target.value })}
              autoComplete="new-password"
            />
            <PasswordPolicyHints
              password={passwordForm.password}
              confirmation={passwordForm.password_confirmation}
              t={t}
            />
            <FormControlLabel
              sx={{
                ml: 0,
                mr: 0,
                gap: 1,
                alignItems: 'center',
                '& .MuiFormControlLabel-label': {
                  fontFamily: typographyTokens.fontFamily,
                  fontSize: 13,
                  color: semantic.text.secondary,
                },
              }}
              control={(
                <Checkbox
                  checked={passwordForm.revoke_other_sessions}
                  onChange={(e) => setPasswordForm({
                    ...passwordForm,
                    revoke_other_sessions: e.target.checked,
                  })}
                  sx={checkboxSx}
                />
              )}
              label={t('settings:logout_other_sessions')}
            />
            <Box>
              <Button
                variant="Accent"
                onClick={handlePasswordChange}
                disabled={passwordInvalid}
              >
                {t('settings:change_password')}
              </Button>
            </Box>
          </Box>
        </SectionCard>

        <SectionCard variant="Default" title={t('settings:two_factor_section')} sx={{ mb: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
            <Typography sx={noticeSx}>
              {t('settings:two_factor_status')}
            </Typography>
            <StatusBadge
              status={twoFactorEnabled ? 'enabled' : 'disabled'}
              category="generic"
              apiLabel={twoFactorEnabled
                ? t('settings:two_factor_enabled')
                : t('settings:two_factor_disabled')}
              size="Sm"
              tone={twoFactorEnabled ? 'Success' : 'Neutral'}
            />
          </Stack>

          {!twoFactorEnabled && !twoFactorSetup && (
            <Button variant="Accent" onClick={handleSetupTwoFactor}>
              {t('settings:setup_two_factor')}
            </Button>
          )}

          {twoFactorSetup && (
            <Box sx={{ display: 'grid', gap: 2, maxWidth: 560 }}>
              <Alert severity="info">
                {t('settings:two_factor_setup_hint')}
              </Alert>
              <TextInput
                density="Compact"
                label={t('settings:secret')}
                value={twoFactorSetup.secret}
                readOnly
              />
              <TextInput
                density="Compact"
                label={t('settings:otpauth_uri')}
                value={twoFactorSetup.otpauth_uri}
                readOnly
              />
              <TextInput
                density="Compact"
                label={t('settings:confirm_code')}
                value={confirmCode}
                onChange={(e) => setConfirmCode(e.target.value)}
              />
              <Box>
                <Button
                  variant="Accent"
                  onClick={handleConfirmTwoFactor}
                  disabled={confirmCode.length !== 6}
                >
                  {t('settings:confirm_enable_2fa')}
                </Button>
              </Box>
            </Box>
          )}

          {recoveryCodes.length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {t('settings:recovery_codes_once')}: {recoveryCodes.join(', ')}
            </Alert>
          )}

          {twoFactorEnabled && (
            <Box sx={{ display: 'grid', gap: 2, maxWidth: 480, mt: 2 }}>
              <TextInput
                density="Compact"
                type="password"
                label={t('settings:disable_2fa_password')}
                value={disablePassword}
                onChange={(e) => setDisablePassword(e.target.value)}
              />
              <Box>
                <Button variant="Danger" onClick={handleDisableTwoFactor}>
                  {t('settings:disable_two_factor')}
                </Button>
              </Box>
              <TextInput
                density="Compact"
                type="password"
                label={t('settings:regenerate_codes_password')}
                value={regeneratePassword}
                onChange={(e) => setRegeneratePassword(e.target.value)}
              />
              <Box>
                <Button variant="Secondary" onClick={handleRegenerateRecoveryCodes}>
                  {t('settings:regenerate_recovery_codes')}
                </Button>
              </Box>
            </Box>
          )}
        </SectionCard>

        <SectionCard variant="Default" noPadding sx={{ mb: 0, width: '100%' }}>
          <TableToolbar
            mode="Default"
            title={t('settings:active_sessions')}
            meta={(
              <StatusBadge
                status="list"
                category="generic"
                apiLabel={t('settings:sessions_toolbar_count', { count: sessions.length })}
                size="Sm"
                tone="Neutral"
              />
            )}
            actions={(
              <Button
                variant="Danger"
                onClick={handleLogoutOtherSessions}
                loading={loggingOutOthers}
                disabled={loggingOutOthers}
                sx={{ minHeight: 32, px: 1.5, fontSize: 12 }}
              >
                {t('settings:logout_other_sessions')}
              </Button>
            )}
          />
          {loadingSessions ? (
            <LoadingSkeleton type="Table" embedded rows={3} columns={sessionColumns.length} />
          ) : (
            <DataTable
              type="Default"
              embedded
              columns={sessionColumns}
              rows={sessions}
              getRowKey={(row) => row.id}
              emptyMessage={t('common:no_data_found')}
              sx={{
                overflowX: 'auto',
                width: '100%',
                '& table': { width: '100%', minWidth: '100%' },
              }}
            />
          )}
        </SectionCard>

        <SectionCard variant="Default" noPadding sx={{ mb: 0, width: '100%' }}>
          <TableToolbar
            mode="Default"
            title={t('settings:security_history')}
            meta={(
              <StatusBadge
                status="list"
                category="generic"
                apiLabel={t('settings:events_toolbar_count', { count: events.length })}
                size="Sm"
                tone="Neutral"
              />
            )}
          />
          {loadingEvents ? (
            <LoadingSkeleton type="Table" embedded rows={3} columns={eventColumns.length} />
          ) : (
            <DataTable
              type="Default"
              embedded
              columns={eventColumns}
              rows={events}
              getRowKey={(row) => row.id}
              emptyMessage={t('common:no_data_found')}
              sx={{
                overflowX: 'auto',
                width: '100%',
                '& table': { width: '100%', minWidth: '100%' },
              }}
            />
          )}
        </SectionCard>
      </Stack>
    </Box>
  );
}
